import { castCompatibleValue, isOption, noRestrictions, restrictedTo } from './CompatibleValues.js';
import { pOptionTypeGuard } from '../engine.js';
import { PropertyPathWalker } from '../internal/PropertyPathWalker.js';
import { objectTypeTypeGuard } from '../types.js';
import { optionIdSymbol } from '../engine-impl.js';
import { TypeService } from '../services/TypeService.js';
import { engineUtils } from '../services/EngineUtils.js';
function isModelValueOption(modelValue) {
    return !!modelValue[optionIdSymbol];
}
export class CompatibilityTable {
    constructor(structure, schema, typeRef) {
        this.structure = structure;
        this.schema = schema;
        this.typeRef = typeRef;
        this.rules = [];
        this.columnName = new Map();
        this.externalFieldRegistry = null;
        this.validHashes = new Set();
        this.whenExpression = () => true;
        this.optionsColumns = new Map();
        this.typeService = new TypeService(this.typeRef, this.schema);
        for (let i = 0; i < structure.columns.length; i++) {
            this.columnName.set(structure.columns[i], i);
        }
        this.buildOptionColumns();
    }
    setWhen(when) {
        this.whenExpression = when;
    }
    injectExternalFieldRegistry(registry) {
        this.externalFieldRegistry = registry;
    }
    addCompatibilityRules(...rules) {
        for (let i = 0; i < rules.length; i++) {
            try {
                let rule = rules[i];
                this.validateRule(rule);
                this.rules.push(rule);
                this.addRuleHash(rule);
            }
            catch (e) {
                if (e instanceof Error) {
                    throw new Error(`Invalid ${i} rule. ${e.message}`);
                }
                else {
                    throw e;
                }
            }
        }
    }
    validateRule(rule) {
        if (rule.length < this.structure.columns.length) {
            throw new Error(`Invalid count of values. ${this.structure.columns.length} expected but received ${rule.length}`);
        }
    }
    getAvailableCompatibleValues(propertyPath) {
        const values = new Set();
        for (let rule of this.rules) {
            const rulePropertyValue = this.findPropertyPathValue(rule, propertyPath);
            if (rulePropertyValue !== undefined) {
                values.add(rulePropertyValue);
            }
        }
        let restrictedToValues = castCompatibleValue([...values]);
        return values.size > 0 ? restrictedTo(restrictedToValues) : noRestrictions();
    }
    getCompatibleWithModelValues(model, propertyPath) {
        if (this.isCompatibilitySwitchedOff(model)) {
            return noRestrictions();
        }
        this.validateModelPropertyPath(model, propertyPath);
        const modelRow = this.getModelRow(model);
        const values = new Set();
        for (let rule of this.rules) {
            const rulePropertyValue = this.findPropertyPathValue(rule, propertyPath);
            if (rulePropertyValue !== undefined && this.modelSatisfiesRule(modelRow, rule, propertyPath)) {
                values.add(rulePropertyValue);
            }
        }
        let restrictedToValues = castCompatibleValue([...values]);
        return values.size > 0 ? restrictedTo(restrictedToValues) : noRestrictions();
    }
    getModelRow(model) {
        return this.structure.columns.map(columnName => {
            const propertyPathWalker = new PropertyPathWalker(columnName, this.schema, model, this.typeRef);
            const propertyValue = propertyPathWalker.getPropertyValue();
            if (propertyValue) {
                return propertyValue.value;
            }
            if (this.isExternalField(columnName)) {
                return this.getExternalFieldValue(model, columnName);
            }
            else {
                throw new TypeError(`Invalid propertyPath ${columnName}`);
            }
        });
    }
    getModelRowValue(model) {
        return this.getModelRow(model).map(v => isModelValueOption(v) ? v[optionIdSymbol] : v);
    }
    getRuleRowValue(rule) {
        return rule.map(v => isOption(v) ? v._id : v);
    }
    findPropertyPathValue(rule, propertyPath) {
        const index = this.columnName.get(propertyPath);
        return rule[index];
    }
    modelSatisfiesRule(modelValues, rule, ignoreProperty) {
        for (let i = 0; i < this.structure.columns.length; i++) {
            const key = this.structure.columns[i];
            let ruleValue = rule[i];
            let modelValue = modelValues[i];
            if (key !== ignoreProperty) {
                if (isOption(ruleValue)) {
                    if (isModelValueOption(modelValue)) {
                        ruleValue = ruleValue._id;
                        modelValue = modelValue[optionIdSymbol];
                    }
                    else
                        return false;
                }
                if (ruleValue !== modelValue) {
                    return false;
                }
            }
        }
        return true;
    }
    validateModelPropertyPath(model, propertyPath) {
        const propertyPathWalker = new PropertyPathWalker(propertyPath, this.schema, model, this.typeRef);
        const propertyValue = propertyPathWalker.getPropertyValue();
        if (propertyValue === undefined && !this.isExternalField(propertyPath)) {
            throw new TypeError(`Invalid propertyPath ${propertyPath}`);
        }
    }
    isExternalField(columnName) {
        return this.typeService.isPropertyExternal(columnName);
    }
    getExternalFieldValue(model, columnName) {
        if (this.externalFieldRegistry) {
            return this.externalFieldRegistry.getExternalFieldValue(model, columnName);
        }
        throw new Error(`External wasn't injected into CompatibilityTable`);
    }
    validate(model) {
        if (this.isCompatibilitySwitchedOff(model)) {
            return null;
        }
        const modelValue = this.getModelRowValue(model);
        const modelHash = this.getHash(modelValue);
        return this.validHashes.has(modelHash) ? null : modelHash;
    }
    getHash(row) {
        return row.map(v => isOption(v) ? v._id : v).join('##');
    }
    addRuleHash(rule) {
        const flatValue = this.getRuleRowValue(rule);
        const ruleHash = this.getHash(flatValue);
        this.validHashes.add(ruleHash);
    }
    isCompatibilitySwitchedOff(model) {
        return !this.whenExpression(model);
    }
    getCompatibilityInvolvedOptionPaths() {
        return new Set(this.optionsColumns.keys());
    }
    getColumnOptionPath(column) {
        let optionPath = '';
        this.typeService.findTypeInPropertyPath(column, (type, parentType, propertyName, path) => {
            if (parentType && objectTypeTypeGuard(type) && objectTypeTypeGuard(parentType)) {
                const sourcedValueProperty = parentType.properties.find(property => property.name == propertyName && property.opts.needSource);
                if (sourcedValueProperty) {
                    optionPath = TypeService.absolutePathToRelative(path);
                    return true;
                }
            }
            return false;
        });
        return optionPath;
    }
    createOptionPropertiesSelector(optionPath) {
        const optionFullPathProperties = this.optionsColumns.get(optionPath);
        const optionProperties = [];
        for (const column of optionFullPathProperties) {
            const shortColumnPath = column.slice(optionPath.length + 1);
            optionProperties.push(shortColumnPath);
        }
        const ruleIndexes = [];
        for (let column of optionFullPathProperties) {
            ruleIndexes.push(this.columnName.get(column));
        }
        return {
            ruleHashSelector: (rule) => {
                const valueForHash = [];
                for (let i of ruleIndexes) {
                    const ruleValue = rule[i];
                    if (pOptionTypeGuard(ruleValue)) {
                        valueForHash.push(ruleValue._id);
                    }
                    else {
                        valueForHash.push(ruleValue);
                    }
                }
                return this.getHash(valueForHash);
            },
            optionHashSelector: option => {
                const valuesForHash = [];
                for (let column of optionProperties) {
                    if (column === '' && pOptionTypeGuard(option)) {
                        valuesForHash.push(option._id);
                    }
                    else {
                        valuesForHash.push(engineUtils.getPropertyPathValue(option.model, column));
                    }
                }
                return this.getHash(valuesForHash);
            }
        };
    }
    buildOptionColumns() {
        const paths = new Set();
        for (let column of this.columnName.keys()) {
            const optionPath = this.getColumnOptionPath(column);
            if (optionPath) {
                let columns = this.optionsColumns.get(optionPath);
                if (!columns) {
                    columns = new Set();
                    this.optionsColumns.set(optionPath, columns);
                }
                columns.add(column);
            }
        }
        return paths;
    }
}
//# sourceMappingURL=CompatibilityTable.js.map